import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Lock, PhoneCall } from "lucide-react";
import TurnstileWidget from "@/components/TurnstileWidget";
import { getUtm } from "@/lib/utm";
import PhoneInput from "@/components/PhoneInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRY_OPTIONS = [
  "Consulting", "Healthcare", "Gas Stations", "Landscaping", "Restaurants",
  "Retail", "Transportation", "Construction", "Other",
];
const TIB_OPTIONS = ["Less than 3 months", "3 – 6 months", "6 – 12 months", "1 – 2 years", "2 – 5 years", "5+ years"];
const DEPOSIT_OPTIONS = [
  "Under $5,000", "$5,000 – $15,000", "$15,000 – $25,000", "$25,000 – $50,000",
  "$50,000 – $100,000", "$100,000 – $200,000", "$200,000 – $500,000", "$500,000+",
];

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "", company: "",
  industry: "", time_in_business: "", monthly_deposits: "", revenue_in_bank: "",
  loan_amount: "", message: "", consent_sms: false,
};

const GIGGLE_MAX = 15000;
const parseAmount = (s) => parseFloat(String(s).replace(/[^0-9.]/g, "")) || 0;
const formatAmount = (s) => {
  const raw = String(s).replace(/[^0-9.]/g, "");
  if (!raw) return "";
  const [intPart, ...dec] = raw.split(".");
  const intFmt = intPart ? Number(intPart).toLocaleString("en-US") : "";
  return dec.length ? `${intFmt}.${dec.join("").slice(0, 2)}` : intFmt;
};

export default function LeadForm({ withMessage = false, title = "Get pre-qualified now" }) {
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [contactId, setContactId] = useState("");
  const [choice, setChoice] = useState(""); // "" | contacted
  const [choiceBusy, setChoiceBusy] = useState("");
  const [appEmail, setAppEmail] = useState("");
  const [needEmail, setNeedEmail] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.phone.trim() || !form.company.trim()) {
      toast.error("First name, phone and company are required");
      return;
    }
    setBusy(true);
    try {
      const amt = parseAmount(form.loan_amount);
      const { data } = await axios.post(`${API}/public/website-lead`, {
        ...form,
        email: form.email.trim() || null,
        loan_amount: amt || null,
        giggle_referral: amt > 0 && amt <= GIGGLE_MAX,
        turnstile_token: turnstileToken,
        source_path: window.location.pathname,
        utm_source: getUtm().source,
        utm_medium: getUtm().medium,
        utm_campaign: getUtm().campaign,
        city_source: (() => { try { return sessionStorage.getItem("ecc_city_source") || ""; } catch (_e) { return ""; } })(),
      });
      if (amt > 0 && amt <= GIGGLE_MAX) {
        toast.success("Good news — you may qualify for fast funding. Taking you to our funding partner…");
        setTimeout(() => { window.location.href = `${API}/public/giggle-click?src=prequal`; }, 1500);
        return;
      }
      setContactId(data.contact_id || "");
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong — please call us at (888) 550-9339");
    } finally {
      setBusy(false);
    }
  }

  async function contactMe() {
    setChoiceBusy("contact");
    try {
      if (contactId) await axios.post(`${API}/public/leads/${contactId}/contact-me`);
    } catch (_e) { /* confirmation regardless — staff already have the lead */ }
    setChoice("contacted");
    setChoiceBusy("");
  }

  async function startApplication() {
    const email = (form.email || appEmail).trim();
    if (!email) {
      setNeedEmail(true);
      toast.error("Enter your email so we can send your application number and secure link");
      return;
    }
    setChoiceBusy("start");
    try {
      const { data } = await axios.post(`${API}/public/leads/${contactId}/start-application`, { email });
      toast.success(`Application ${data.application_number} created — confirmation sent to your email`);
      nav(`/apply/${data.token}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "string" && d.toLowerCase().includes("email")) setNeedEmail(true);
      toast.error(typeof d === "string" ? d : "Could not start your application — please call (888) 550-9339");
    } finally {
      setChoiceBusy("");
    }
  }

  if (done) {
    if (choice === "contacted") {
      return (
        <div className="surface p-8 text-center" style={{ borderTop: "3px solid var(--success)" }} data-testid="lead-form-contacted">
          <PhoneCall size={36} strokeWidth={1.5} color="var(--success)" className="mx-auto" />
          <h3 className="font-display text-xl mt-4" style={{ color: "var(--ink)" }}>We'll be in touch</h3>
          <p className="text-sm mt-2" style={{ color: "var(--muted-fg)" }}>
            A funding specialist will call you shortly{form.phone ? ` at ${form.phone}` : ""}. Prefer not to wait?
            Call us now at <a href="tel:8885509339" style={{ color: "var(--primary)", fontWeight: 600 }}>(888) 550-9339</a>.
          </p>
        </div>
      );
    }
    return (
      <div className="surface p-8 text-center" style={{ borderTop: "3px solid var(--success)" }} data-testid="lead-form-success">
        <CheckCircle2 size={36} strokeWidth={1.5} color="var(--success)" className="mx-auto" />
        <h3 className="font-display text-xl mt-4" style={{ color: "var(--ink)" }}>Request received</h3>
        <p className="text-sm mt-2" style={{ color: "var(--muted-fg)" }}>
          How would you like to proceed?
        </p>
        {needEmail && !form.email && (
          <div className="field mt-4 text-left">
            <label className="label-eyebrow block mb-1.5">Email for your application number *</label>
            <input
              type="email"
              value={appEmail}
              onChange={(e) => setAppEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="lead-app-email"
            />
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-5">
          <button
            type="button"
            className="btn-ghost"
            onClick={contactMe}
            disabled={!!choiceBusy}
            data-testid="lead-contact-me-btn"
          >
            <PhoneCall size={14} /> {choiceBusy === "contact" ? "One moment…" : "Contact me"}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={startApplication}
            disabled={!!choiceBusy}
            data-testid="lead-start-application-btn"
          >
            {choiceBusy === "start" ? "Starting…" : <>Start full application <ArrowRight size={14} /></>}
          </button>
        </div>
        <p className="text-[11px] mt-4" style={{ color: "var(--muted-fg)" }}>
          Starting the full application gets you an application number instantly — we'll email it to you with a secure return link.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="surface p-6 sm:p-8" style={{ borderTop: "3px solid var(--primary)" }} data-testid="lead-form">
      <p className="text-xs font-mono uppercase tracking-[0.2em] font-bold" style={{ color: "var(--primary)" }}>{title}</p>
      <h3 className="font-display text-2xl mt-1.5" style={{ color: "var(--ink)", fontWeight: 500 }}>
        Approved in as soon as 2 hours.
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
        <Field label="First name *"><input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="lead-first-name" autoComplete="given-name" /></Field>
        <Field label="Last name"><input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="lead-last-name" autoComplete="family-name" /></Field>
        <Field label="Email"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-email" autoComplete="email" /></Field>
        <Field label="Phone *"><PhoneInput value={form.phone} onChange={(v) => set("phone", v)} testId="lead-phone" /></Field>
        <Field label="Company name *" full><input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="lead-company" autoComplete="organization" /></Field>
        <Field label="Requested loan amount">
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted-fg)", fontSize: 14, pointerEvents: "none" }}>$</span>
            <input
              inputMode="decimal"
              value={form.loan_amount}
              onChange={(e) => set("loan_amount", formatAmount(e.target.value))}
              placeholder="50,000.00"
              style={{ paddingLeft: 26 }}
              data-testid="lead-loan-amount"
            />
          </div>
        </Field>
        <Field label="Industry">
          <select value={form.industry} onChange={(e) => set("industry", e.target.value)} data-testid="lead-industry">
            <option value="">Select…</option>
            {INDUSTRY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Time in business">
          <select value={form.time_in_business} onChange={(e) => set("time_in_business", e.target.value)} data-testid="lead-tib">
            <option value="">Select…</option>
            {TIB_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Avg monthly deposits">
          <select value={form.monthly_deposits} onChange={(e) => set("monthly_deposits", e.target.value)} data-testid="lead-deposits">
            <option value="">Select…</option>
            {DEPOSIT_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Revenue into a business bank account?">
          <select value={form.revenue_in_bank} onChange={(e) => set("revenue_in_bank", e.target.value)} data-testid="lead-revenue-bank">
            <option value="">Select…</option>
            <option>Yes</option>
            <option>No</option>
          </select>
        </Field>
        {withMessage && (
          <Field label="How can we help?" full>
            <textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="lead-message" />
          </Field>
        )}
      </div>

      <label className="flex items-start gap-2 mt-4 text-[11px] cursor-pointer" style={{ color: "var(--muted-fg)" }}>
        <input type="checkbox" checked={form.consent_sms} onChange={(e) => set("consent_sms", e.target.checked)} className="mt-0.5" data-testid="lead-sms-consent" />
        I agree to receive text messages about my financing request. By providing contact
        information you consent to our privacy practices.
      </label>

      <div className="mt-4 flex justify-center">
        <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken("")} onError={() => setTurnstileToken("")} testId="lead-turnstile" />
      </div>

      <button type="submit" className="btn-primary w-full mt-4" disabled={busy || !turnstileToken} data-testid="lead-submit-btn">
        {busy ? "Submitting…" : <>Get my options <ArrowRight size={14} /></>}
      </button>
      <p className="flex items-center gap-1.5 justify-center mt-3 text-[11px]" style={{ color: "var(--muted-fg)" }}>
        <Lock size={11} strokeWidth={1.5} /> No impact to your credit score to get options.
      </p>
    </form>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`field ${full ? "sm:col-span-2" : ""}`}>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
